import * as _ from 'lodash';
// tslint:disable-next-line:no-submodule-imports
import { all, call, Effect, put, putResolve, select } from 'redux-saga/effects';
import { Application, ApplicationCollection, Server2, Workspace3 } from '../../clients/aasServerClient.generated';
import * as ApplicationRepository from '../../repositories/ApplicationRepository';
import { takeEveryWithErrorHandling } from '../sagas';
import { unselectAll} from '../tables/actionCreators';
import { RootState } from '../store';
import { storePagingState } from '../tables/actionCreators';
import { getFilters, getLimit, getPaging, getSearch, getSorting, getStart } from '../tables/selectors';
import { TableFilters, TablePaging, TableSorting } from '../tables/state';
import {
    fetchAllApplications as fetchAllApplicationsCreator,
    fetchTableApplications as fetchApplicationsCreator,
    savedSingleApplication,
    storeAllApplications,
    storeSingleApplication,
    storeTableApplications,
} from './actionCreators';
import {
    ActionTypeNames, AssignApplicationsWorkspaceAction, CopySingleApplicationAction,
    CreateSingleApplicationAction,
    DeleteApplicationsAction,
    FetchSingleApplicationAction,
    NewSingleApplicationAction,
    SaveSingleApplicationAction,
    SynchronizeUsersAction,
} from './actions';

function* fetchTableApplications() {
    const start: number = yield select(getStart, 'applications');
    const limit: number = yield select(getLimit, 'applications');
    const sorting: TableSorting = yield select(getSorting, 'applications');
    const filters: TableFilters = yield select(getFilters, 'applications');
    const paging: TablePaging = yield select(getPaging, 'applications');
    const search: string = yield select(getSearch, 'applications');

    const res: ApplicationCollection = yield call(
        ApplicationRepository.getApplications,
        limit, start, sorting, filters, search
    );
    yield all([
        putResolve(storeTableApplications(res.items)),
        putResolve(storePagingState('applications', {
            numberOfItems: res.count || 0,
            numberOfPages: Math.ceil((res.count || 0) / paging.itemsPerPage)
        }))
    ]);
}


function* fetchTableApplicationsNF() {
    const start: number = yield select(getStart, 'applications');
    const limit: number = yield select(getLimit, 'applications');
    const sorting: TableSorting = yield select(getSorting, 'applications');
    
    const paging: TablePaging = yield select(getPaging, 'applications');
    

    const res: ApplicationCollection = yield call(
        ApplicationRepository.getApplicationsNF,
        limit, start, sorting,  undefined
    );
    yield all([
        putResolve(storeTableApplications(res.items)),
        putResolve(storePagingState('applications', {
            numberOfItems: res.count || 0,
            numberOfPages: Math.ceil((res.count || 0) / paging.itemsPerPage)
        }))
    ]);
}


export function* watchFetchTableApplications() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchTableApplications, fetchTableApplications);
}

export function* watchFetchTableApplicationsNF() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchTableApplicationsNF, fetchTableApplicationsNF);
}

function* fetchAllApplications() {
    const res: ApplicationCollection = yield call(ApplicationRepository.getAllApplications);
    yield putResolve(storeAllApplications(res.items));
}

export function* watchFetchAllApplications() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchAllApplications, fetchAllApplications);
}

function* fetchSingleApplication(action: FetchSingleApplicationAction) {
    const application: Application = yield call(ApplicationRepository.getApplication, action.id);
    yield putResolve(storeSingleApplication(application));
}

function* watchFetchSingleApplication() {
    yield takeEveryWithErrorHandling(ActionTypeNames.FetchSingleApplication, fetchSingleApplication);
}

function* copySingleApplication(action: CopySingleApplicationAction) {
    const res: Application = yield call(ApplicationRepository.getApplication, action.id);
    const application: Partial<Application> = {
        color: res.color,
        help_url: res.help_url,
        location_path: res.location_path,
        server: res.server,
        server_alias: res.server_alias,
        title: '',
        workspace: res.workspace,
        no_login_as: res.no_login_as,
    };
    yield putResolve(storeSingleApplication(application));
}

function* watchCopySingleApplication() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CopySingleApplication, copySingleApplication);
}

function* newSingleApplication(action: NewSingleApplicationAction) {
    const application: Partial<Application> = {
        color: '',
        help_url: '',
        location_path: '',
        no_login_as: false,
        server: new Server2(),
        server_alias: '',
        title: '',
        workspace: new Workspace3({ id: '' }),
    };
    yield putResolve(storeSingleApplication(application));
}

function* watchNewSingleApplication() {
    yield takeEveryWithErrorHandling(ActionTypeNames.NewSingleApplication, newSingleApplication);
}

function* saveSingleApplication(action: SaveSingleApplicationAction) {
    const res = yield call(
        ApplicationRepository.updateApplication,
        action.application, action.workspaceId, action.serverId
    );
    yield putResolve(savedSingleApplication());
    yield all([
        put(fetchApplicationsCreator()),
        put(fetchAllApplicationsCreator()),
    ]);
}

function* watchSaveSingleApplication() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SaveSingleApplication, saveSingleApplication);
}

function* createSingleApplication(action: CreateSingleApplicationAction) {
    const res = yield call(
        ApplicationRepository.createApplication,
        action.application,
        action.workspaceId, action.serverId
    );
    yield putResolve(savedSingleApplication());
    yield all([
        put(fetchApplicationsCreator()),
        put(fetchAllApplicationsCreator()),
    ]);
}

function* watchCreateSingleApplication() {
    yield takeEveryWithErrorHandling(ActionTypeNames.CreateSingleApplication, createSingleApplication);
}

function* watchDeleteApplications() {
    yield takeEveryWithErrorHandling(ActionTypeNames.DeleteApplications, deleteApplications);
}

function* deleteApplications(action: DeleteApplicationsAction) {
    yield call(ApplicationRepository.deleteApplications, action.applicationIds);
    yield put(unselectAll('applications',action.applicationIds)) ;   
    yield putResolve(fetchApplicationsCreator());
}

function* watchSynchronizeUsers() {
    yield takeEveryWithErrorHandling(ActionTypeNames.SynchronizeUsers, synchronizeUsers);
}

function* synchronizeUsers(action: SynchronizeUsersAction) {
    yield call(ApplicationRepository.synchronize, action.applicationIds);
    yield putResolve(fetchApplicationsCreator());
}

function* watchAssignApplicationsWorkspace() {
    yield takeEveryWithErrorHandling(ActionTypeNames.AssignApplicationsWorkspace, assignApplicationsWorkspace);
}

function* assignApplicationsWorkspace(action: AssignApplicationsWorkspaceAction) {
    yield call(ApplicationRepository.assignApplicationsWorkspace, action.applicationIds, action.workspaceId);
    yield putResolve(fetchApplicationsCreator());
}

export default [watchFetchTableApplicationsNF,watchFetchTableApplications, watchFetchAllApplications,
    watchDeleteApplications, watchSynchronizeUsers, watchAssignApplicationsWorkspace, watchCopySingleApplication,
    watchFetchSingleApplication, watchSaveSingleApplication, watchCreateSingleApplication, watchNewSingleApplication];